/**
 * useEqVasTranslations hook
 *
 * V2 Live Testing에서 eq_vas 질문의 topLabel, bottomLabel, valueBoxLabel 번역을 로드하고
 * PaDataRepoState.translations에 설정하는 hook
 */

import { useEffect, useMemo, useRef } from 'react';
import { useSetRecoilState } from 'recoil';
import { translationControllerSearch } from '../../../../api/generated/translation/translation';
import type { SearchTranslationResponseDto } from '../../../../api/generated/types';
import { PaDataRepoState } from '../../../../../report/use-data';
import {
  genEqVasTopLabelKey,
  genEqVasBottomLabelKey,
  genEqVasValueBoxLabelKey,
} from '../questions/useQuestionConfigureModal';

interface UseEqVasTranslationsProps {
  surveyId?: string | number;
  /** eq_vas 타입 질문들의 questionKey 목록 */
  eqVasQuestionKeys: string[];
  locale: string;
  enabled?: boolean;
}

export function useEqVasTranslations({
  surveyId,
  eqVasQuestionKeys,
  locale,
  enabled = true,
}: UseEqVasTranslationsProps) {
  const setDataState = useSetRecoilState(PaDataRepoState);
  const loadedRef = useRef<string | null>(null);

  // 배열 참조 안정화 - join된 문자열 기반으로 memoize
  const stableKeysStr = eqVasQuestionKeys.join(',');
  const stableKeys = useMemo(
    () => (stableKeysStr ? stableKeysStr.split(',') : []),
    [stableKeysStr],
  );

  useEffect(() => {
    if (!enabled || !surveyId || !stableKeys.length || !locale) {
      return;
    }

    const cacheKey = `eqvas-${surveyId}-${stableKeysStr}-${locale}`;
    if (loadedRef.current === cacheKey) {
      return;
    }
    // 즉시 캐시 설정하여 중복 호출 방지
    loadedRef.current = cacheKey;

    // EQ-VAS 번역 키 목록 생성
    const keys: string[] = [];
    stableKeys.forEach((qKey) => {
      keys.push(genEqVasTopLabelKey(qKey));
      keys.push(genEqVasBottomLabelKey(qKey));
      keys.push(genEqVasValueBoxLabelKey(qKey));
    });

    async function loadTranslations() {
      try {
        const response = (await translationControllerSearch({
          surveyId: Number(surveyId),
          keys,
          locale,
          type: 'patient',
        })) as SearchTranslationResponseDto;

        const apiTranslations = response?.translations || [];

        if (apiTranslations.length > 0) {
          const formattedTranslations = apiTranslations.map((t) => ({
            id: t.id?.toString() || null,
            key: t.key || '',
            value: t.value || null,
            locale: t.locale || null,
            type: null,
            dataType: null,
          }));

          setDataState((prev) => {
            const existingTranslations = prev.translations || [];
            const newTranslationsMap = new Map<string, (typeof prev.translations)[number]>();

            existingTranslations.forEach((tr) => {
              const uniqueKey = `${tr.key}-${tr.locale}`;
              newTranslationsMap.set(uniqueKey, tr);
            });

            formattedTranslations.forEach((tr) => {
              const uniqueKey = `${tr.key}-${tr.locale}`;
              newTranslationsMap.set(uniqueKey, tr as (typeof prev.translations)[number]);
            });

            return {
              ...prev,
              translations: Array.from(newTranslationsMap.values()),
            };
          });
        }
      } catch (error) {
        console.error('Failed to load EQ-VAS translations:', error);
        // 실패 시 캐시 리셋하여 재시도 가능하게
        loadedRef.current = null;
      }
    }

    loadTranslations();
  }, [surveyId, stableKeys, stableKeysStr, locale, enabled, setDataState]);
}
